/**
 * Folder picker, shared by create and edit. Existing folders come from
 * GET /api/folders; "New folder…" reveals a text box.
 *
 * This used to be create-only, on the theory that a note's folder was owned by
 * the file layout afterwards and changing it here would fight the sync daemon.
 * That is not how the daemon behaves: a folder change on the server is a move
 * (`syncer.targetPath` re-files the note under its existing filename and
 * removes the old file), which is exactly what a user changing the folder in
 * the editor wants. The one thing the two modes still differ on is the hint,
 * because "where the file is created" is the wrong sentence for a move.
 */

import { el } from "../lib/dom";

const NEW_FOLDER_VALUE = "__new__";

export interface FolderSelectOptions {
  readonly folders: readonly string[];
  readonly initial: string;
  /** Sentence under the control; defaults to the create-mode wording. */
  readonly hint?: string;
}

const DEFAULT_HINT = "Where the note file is created.";

export interface FolderSelectHandle {
  readonly element: HTMLElement;
  readonly getValue: () => string;
  /** Focuses the dropdown and pops its option list open. */
  readonly open: () => void;
}

export function folderSelect(options: FolderSelectOptions): FolderSelectHandle {
  const known = options.folders.includes(options.initial)
    ? options.folders
    : [options.initial, ...options.folders].filter((folder) => folder !== "");

  const select = el("select", {
    className: "input input--select",
    attrs: { name: "folder", id: "field-folder" },
    children: [
      ...known.map((folder) =>
        el("option", { attrs: { value: folder, selected: folder === options.initial }, text: folder }),
      ),
      el("option", { attrs: { value: NEW_FOLDER_VALUE }, text: "New folder…" }),
    ],
  });

  const customInput = el("input", {
    className: "input",
    attrs: { type: "text", placeholder: "folder/sub-folder", "aria-label": "New folder name", autocomplete: "off" },
  });
  customInput.hidden = true;

  select.addEventListener("change", () => {
    const isNew = select.value === NEW_FOLDER_VALUE;
    customInput.hidden = !isNew;
    if (isNew) {
      customInput.focus();
    }
  });

  const element = el("div", {
    className: "field",
    dataset: { area: "folder" },
    children: [
      el("label", { className: "field__label", attrs: { for: "field-folder" }, text: "Folder" }),
      select,
      customInput,
      el("p", { className: "field__hint", text: options.hint ?? DEFAULT_HINT }),
    ],
  });

  return {
    element,
    open(): void {
      select.focus();
      // showPicker needs a user gesture (a keydown counts) and is absent in
      // older browsers; focus alone is the graceful fallback.
      try {
        select.showPicker();
      } catch {
        /* focused, just not expanded */
      }
    },
    getValue(): string {
      if (select.value === NEW_FOLDER_VALUE) {
        // A blank "new folder" box means the user changed their mind, not
        // that they want a folder called "". Falling back to `initial` keeps
        // create mode on its default and leaves an edited note where it was.
        const custom = customInput.value.trim();
        return custom === "" ? options.initial : custom;
      }
      return select.value;
    },
  };
}
